import { useMemo } from 'react';
import { Table2 } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceText } from '@/components/features/learning/sources/useSourceText';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface CSVViewerProps {
  source: Source;
}

/**
 * Tiny RFC-4180-ish CSV parser - handles double-quoted cells (with
 * embedded delimiters and escaped `""`) and either `,` or `\t` as the
 * delimiter (auto-detected from the first non-quoted line). Plenty for
 * source preview; we don't need a 50KB dependency for this.
 */
function parseDelimited(input: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (inQuotes) {
      if (ch === '"' && input[i + 1] === '"') {
        cell += '"';
        i += 2;
        continue;
      }
      if (ch === '"') {
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      current.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (ch === '\r') {
      i += 1;
      continue;
    }
    if (ch === '\n') {
      current.push(cell);
      rows.push(current);
      current = [];
      cell = '';
      i += 1;
      continue;
    }
    cell += ch;
    i += 1;
  }
  // Trailing cell/row if no trailing newline.
  if (cell.length > 0 || current.length > 0) {
    current.push(cell);
    rows.push(current);
  }
  return rows;
}

const MAX_PREVIEW_ROWS = 1000;

export function CSVViewer({ source }: CSVViewerProps) {
  const { text, isLoading, error } = useSourceText(source.id);

  const { headers, rows, truncated } = useMemo(() => {
    if (!text) return { headers: [], rows: [], truncated: false };
    // .tsv files use tabs; everything else falls back to comma.
    const ext = (source.original_filename || source.name)
      .split('.')
      .pop()
      ?.toLowerCase();
    const delim = ext === 'tsv' ? '\t' : ',';
    const all = parseDelimited(text, delim);
    if (all.length === 0) return { headers: [], rows: [], truncated: false };
    const [head, ...body] = all;
    const truncated = body.length > MAX_PREVIEW_ROWS;
    return {
      headers: head,
      rows: truncated ? body.slice(0, MAX_PREVIEW_ROWS) : body,
      truncated,
    };
  }, [text, source.original_filename, source.name]);

  if (isLoading) return <ViewerLoading />;
  if (error)
    return <ViewerError message="Couldn't load file" detail={error.message} />;
  if (!headers.length)
    return <ViewerError message='File is empty' detail='No rows to display.' />;

  return (
    <ViewerShell sourceId={source.id} icon={Table2} label='CSV' tone='practice'>
      <div className='flex h-full flex-col'>
        <div className='flex-1 overflow-auto'>
          <table className='min-w-full border-collapse text-xs'>
            <thead className='sticky top-0 bg-muted'>
              <tr>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className='whitespace-nowrap border-b border-r border-border px-3 py-2 text-left font-semibold text-foreground last:border-r-0'
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className='even:bg-muted/40'>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className='whitespace-nowrap border-b border-r border-border/60 px-3 py-1.5 text-muted-foreground last:border-r-0'
                      title={cell}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {truncated && (
          <div className='border-t border-border bg-muted px-4 py-2 text-xs text-muted-foreground'>
            Showing first {MAX_PREVIEW_ROWS.toLocaleString()} rows. Download the
            file to see the full dataset.
          </div>
        )}
      </div>
    </ViewerShell>
  );
}
